import React from "react";
import { useStaticQuery, graphql } from "gatsby";

/**
 * SEO head tags. Rendered through the **Gatsby Head API** (`export const Head`
 * on each page/template), not inside the component tree. Page-specific props
 * override the site-wide defaults from the `nav` singleton.
 */
function Seo({
  title,
  description,
  imageOpenGraph,
  imageAlt,
  twitterHandle = "topazenergy",
}) {
  const { nav, site } = useStaticQuery(SEO_DEFAULTS_QUERY);
  const defaults = nav.data;
  const meta = site.siteMetadata;

  // Organization-level structured data (schema.org). Topaz is a publicly traded
  // company, so `Corporation` (+ tickerSymbol) is the most specific type.
  const organizationJsonLd = {
    "@context": "https://schema.org",
    "@type": "Corporation",
    name: meta.title,
    url: meta.siteUrl,
    description: meta.description,
    tickerSymbol: "TSX:TPZ",
  };

  const metaTitle =
    title || (defaults.seo_title && defaults.seo_title.text) || "Topaz Energy";
  const metaDescription =
    description ||
    (defaults.seo_description && defaults.seo_description.text) ||
    "";
  const image =
    imageOpenGraph || (defaults.seo_image && defaults.seo_image.url) || "";
  const imageAltText =
    imageAlt || (defaults.seo_image && defaults.seo_image.alt) || "";
  const imageType =
    image && image.indexOf(".png") !== -1 ? "image/png" : "image/jpeg";

  return (
    <>
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, shrink-to-fit=no, viewport-fit=cover"
      />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:image:secure_url" content={image} />
      <meta property="og:image:alt" content={imageAltText} />
      <meta property="og:image:type" content={imageType} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={imageAltText} />

      {/* Structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationJsonLd) }}
      />
    </>
  );
}

const SEO_DEFAULTS_QUERY = graphql`
  {
    site {
      siteMetadata {
        title
        description
        siteUrl
      }
    }
    nav: prismicNav {
      data {
        seo_title {
          text
        }
        seo_description {
          text
        }
        seo_image {
          url
          alt
        }
      }
    }
  }
`;

export default Seo;
